/**
 * /app/admin — Platform-level admin (Phase 0).
 *
 * Two tabs:
 *  - **Packs**  — mock-mode toggle for industry pack enablement
 *                  (B0.25; flip foundry on/off and watch the Tier-2 nav
 *                  appear/disappear live).
 *  - **Branding** — full white-label editor (B0.39): mode (light/dark/
 *                  system), 6-slot palette, logo upload, typography,
 *                  density, corner radius, background pattern, 6 named
 *                  preset gallery, live preview, reset.
 *
 * Read-only summaries for users / org / billing / audit / GDPR still
 * surface below the tab content so the page feels populated.
 *
 * Persistence: the org's look (palette, logo, typography, density, radius, pattern) is saved with the org
 * through `PUT /v1/platform/branding` by an org admin (lib/theme/org-look); light or dark is the person's own.
 */

import { lazy, Suspense, useEffect, useState, type ChangeEvent } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Clock,
  Building2,
  CheckCircle2,
  Eye,
  Gauge,
  GraduationCap,
  ImagePlus,
  KeyRound,
  Mail,
  Monitor,
  Moon,
  Network,
  PackageOpen,
  Palette,
  RotateCcw,
  Ruler,
  ShieldCheck,
  Sun,
  Trash2,
  Type,
  Upload,
  Users,
  FlaskConical,
  Coins,
} from "lucide-react";
import { PageContainer } from "@/routes/_layout";
import { PageHeader } from "@/lib/ui/page-header";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/ui/card";
import { Input } from "@/lib/ui/input";
import { Label } from "@/lib/ui/label";
import { Separator } from "@/lib/ui/separator";
import { OrgLookBar } from "@/components/settings/OrgLookBar";
import { ConsultantLimits } from "@/components/settings/ConsultantLimits";
import { UnitsSettings } from "@/components/settings/UnitsSettings";
import { LotDatesSettings } from "@/components/settings/LotDatesSettings";
import { PartCostsImport } from "@/components/settings/PartCostsImport";
import { YourNameCard } from "@/components/settings/YourNameCard";
import { cn } from "@/lib/ui/cn";
import { ApiClientError } from "@/lib/api-client";
import { useTenantTheme, useTenantThemeAdmin } from "@/lib/tenant-theme";
import { useOrg } from "@/lib/org-context";
import { useAuth } from "@/lib/auth";
import { SETTINGS_TABS, settingsTabOf, type SettingsTab } from "@/lib/settings/tabs";
import { scrollTargetOf } from "@/lib/brief/money-rank";
import { PlantTimeSettings } from "@/routes/platform/plant-time";
import { BriefEmailSettings } from "@/routes/twin/brief-email";
import { StationAlertSettings } from "@/components/twin/alerts/StationAlertSettings";
import { NotificationsSettings } from "@/components/iot/NotificationsSettings";
import { AgentKeysPanel } from "@/routes/twin/import-hub";
import { PlantAgentsPanel } from "@/routes/platform/plant-agents";
import { useActivePlantQuery, useEnabledPacksQuery } from "@/lib/api/queries";
import { CatalogUpgradeCard } from "@/components/twin/CatalogUpgradeCard";
import { ImprovementProjectsSwitch } from "@/components/twin/improvement-projects/ImprovementProjectsSwitch";
import {
  isLiveApi,
  invitesApi,
  membersApi,
  orgsApi,
  sitesApi,
  useThingsQuery,
  twinPlants,
  TWIN_ENTITIES_KEY,
  TWIN_CONNECTIONS_KEY,
  TWIN_BATCHES_KEY,
  TWIN_BATCHES_ALL_KEY,
  TWIN_BATCH_PARTS_KEY,
  TWIN_STAGES_KEY,
  backendErrorMessage,
  type VendorVisibility,
} from "@/lib/api";
import { archetypeById } from "@/lib/plant/catalog/archetypes";
import { TeamsPanel } from "@/components/team/TeamsPanel";
import { PrivacyPanel } from "@/routes/platform/knowledge-sharing-panel";
import { ConfirmDialog, DangerConfirmDialog } from "@/lib/ui/confirm-dialog";
import { useToast } from "@/lib/ui/toast";
import {
  DENSITY_SCALE,
  PATTERN_LABEL,
  RADIUS_SCALE,
  TYPOGRAPHY_LABEL,
  type BackgroundPattern,
  type DensityPreset,
  type PaletteOverride,
  type RadiusPreset,
  type TypographyPreset,
} from "@/lib/theme/types";

interface PackOption {
  id: string;
  name: string;
  description: string;
  ships: string;
  tier: "Tier 2";
}

const AVAILABLE_PACKS: PackOption[] = [
  {
    id: "foundry",
    // The pack is the industry vertical (foundry), NOT a specific metal — the
    // org's actual archetype (grey iron / ductile / Al HPDC / …) is surfaced
    // per-org from its provisioned plant below, so this name stays generic
    // (#118: it used to hardcode "Foundry (grey iron)" for every org).
    name: "Foundry",
    description: "Process graph, batch traceability, correlations, LLM model-authoring AI casting consultant — covers all eight casting archetypes.",
    ships: "v1 — production-ready",
    tier: "Tier 2",
  },
];

/**
 * Resolve a friendly label for the active org's plant archetype (#118).
 *
 * Prefers the real archetype recorded in the `plants` table (migration 0044,
 * exposed via GET /v1/twin/plant). Maps the archetype id → its catalog display
 * name (e.g. "iron-green-sand-auto" → "Iron · automatic green-sand line"). Falls
 * back to a sensible default when the org has no plants row (seeded grey-iron /
 * pre-builder orgs) or carries an id the catalog doesn't know.
 */
function useArchetypeLabel(): { label: string; isDefault: boolean } {
  const { data } = useActivePlantQuery();
  const id = data?.archetype_id;
  if (id) {
    const arch = archetypeById(id);
    if (arch) return { label: arch.name, isDefault: false };
    // An archetype id we don't recognise (newer backend catalog) — show the raw
    // id rather than silently pretending it's the grey-iron default.
    return { label: id, isDefault: false };
  }
  // No plants row, or a row with a NULL archetype_id → the documented fallback.
  return { label: "Iron · automatic green-sand line", isDefault: true };
}

const TAB_ICONS: Record<SettingsTab, typeof Palette> = {
  organisation: Palette,
  people: Users,
  "plant-time": Clock,
  units: Ruler,
  email: Mail,
  security: ShieldCheck,
  devices: KeyRound,
  consultant: Gauge,
  packs: PackageOpen,
  help: GraduationCap,
};

// The guided tours stay out of the main bundle, as the Tutorials page kept them: loaded when the tab opens.
const TutorialsSettings = lazy(() =>
  import("@/routes/tutorials").then((m) => ({ default: m.TutorialsSettings })),
);

function SettingsSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold text-on-surface">{title}</h2>
      {children}
    </section>
  );
}

/**
 * /app/settings: the org's settings on one page, a tab each (the 2026-10-06 next-phase plan, step 3). The Admin page,
 * Plant time and Tutorials land here (their old addresses redirect to their tabs), and who gets which brief and
 * which alarm is set in Email. The tab is in the address (?tab=), so a link names it.
 */
export function SettingsPage() {
  const [params, setParams] = useSearchParams();
  const { canAdmin, activeOrg } = useOrg();
  const tab = settingsTabOf(params.get("tab"), canAdmin);
  // A link names a card by its hash (the briefs' "set a cost"); the app does not scroll to one on its own.
  const target = scrollTargetOf(useLocation().hash);
  useEffect(() => {
    if (target && tab === "organisation") document.getElementById(target)?.scrollIntoView({ block: "start" });
  }, [target, tab, canAdmin]);
  const open = (t: SettingsTab) => {
    const next = new URLSearchParams(params);
    next.set("tab", t);
    setParams(next, { replace: true });
  };

  return (
    <PageContainer className="max-w-5xl">
      <PageHeader
        breadcrumb={
          <>
            <Link to="/app" className="hover:text-on-surface">
              App
            </Link>
            <span aria-hidden>/</span>
            <span className="text-on-surface">Settings</span>
          </>
        }
        title="Settings"
        description="The org's look, its people, its plant time and units, who gets which email, security, agent keys, the consultant's usage, packs and the guided tours."
        actions={<Badge variant="info">{activeOrg?.orgName ?? "Tenant"}</Badge>}
      />

      <div role="tablist" aria-label="Settings" className="flex flex-wrap items-stretch gap-1 border-b border-border">
        {SETTINGS_TABS.filter((t) => !t.admin || canAdmin).map((t) => (
          <TabButton key={t.id} active={tab === t.id} onClick={() => open(t.id)} icon={TAB_ICONS[t.id]} label={t.label} />
        ))}
      </div>

      <div data-settings-tab={tab} className="flex flex-col gap-6">
        {tab === "organisation" && (
          <>
            <BrandingPanel />
            {canAdmin && isLiveApi && <OrgSummaryCards />}
            {/* The org's cost of scrap (G4 / S3.4): the one number that lets a report price the prize in money. */}
            {canAdmin && isLiveApi && <ScrapCostCard />}
            {/* Sandbox to real plant: only on a demo org, for an admin. */}
            {canAdmin && <PromoteSandboxCard />}
            <Separator />
            {/* Destructive, irreversible org-level actions; each control gates itself. */}
            <DangerZone />
          </>
        )}

        {tab === "people" && canAdmin && <TeamsPanel />}

        {tab === "plant-time" && (
          <>
            <PlantTimeSettings />
            {/* What a lot's date is (B1b T3): the briefs word a lot's date by it. */}
            {isLiveApi && (
              <SettingsSection title="Lot dates">
                <LotDatesSettings />
              </SettingsSection>
            )}
          </>
        )}
        {tab === "units" && <UnitsSettings />}

        {tab === "email" && (
          <>
            <SettingsSection title="Who gets which brief">
              <BriefEmailSettings />
            </SettingsSection>
            <SettingsSection title="Who gets the alarms">
              <NotificationsSettings />
            </SettingsSection>
            <SettingsSection title="Station alerts">
              <StationAlertSettings />
            </SettingsSection>
          </>
        )}

        {tab === "security" && (
          <>
            <SecurityPanel canAdmin={canAdmin} />
            <PrivacyPanel />
          </>
        )}

        {tab === "devices" && canAdmin && (
          <>
            <PlantAgentsPanel />
            <AgentKeysPanel />
          </>
        )}

        {tab === "consultant" && <ConsultantLimits orgName={activeOrg?.orgName ?? "the org"} />}

        {tab === "packs" && (
          <>
            <PacksPanel />
            {/* Equipment keeps the definition it was created with: this brings it up to the current catalog. */}
            {canAdmin && isLiveApi && <CatalogUpgradeCard />}
            {/* Improvement projects (2026-10-05): the org's switch, off until an org admin turns it on. */}
            {canAdmin && isLiveApi && <ImprovementProjectsSwitch />}
          </>
        )}

        {tab === "help" && (
          <Suspense fallback={<p className="text-sm text-on-surface-muted">Loading the tours.</p>}>
            <TutorialsSettings />
          </Suspense>
        )}
      </div>
    </PageContainer>
  );
}

// ────────────────────────────────────────────────────────────────────
// Danger zone — clear the process model / delete the org
// ────────────────────────────────────────────────────────────────────

/**
 * Is the current user the OWNER of the active org? `useOrg()` carries the
 * role (admin/member/viewer) but not ownership, so we read the org's member
 * list and match the caller (`is_owner`). Live-mode only — stub mode has no
 * members endpoint, so ownership can't be asserted and the delete-org control
 * stays hidden. Returns false until the fetch resolves.
 */
function useIsActiveOrgOwner(): boolean {
  const { activeOrgId } = useOrg();
  const { user } = useAuth();
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    setIsOwner(false);
    if (!isLiveApi || !activeOrgId || !user) return;
    let cancelled = false;
    membersApi
      .list(activeOrgId)
      .then((res) => {
        if (cancelled) return;
        const me = res.items.find(
          (m) => m.user_id === user.id || m.email === user.email,
        );
        setIsOwner(!!me?.is_owner);
      })
      .catch(() => {
        if (!cancelled) setIsOwner(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeOrgId, user]);

  return isOwner;
}

// ────────────────────────────────────────────────────────────────────
// Sandbox → real plant (S1.4)
// ────────────────────────────────────────────────────────────────────

/**
 * The promotion the onboarding wizard promises.
 *
 * Step 2 of onboarding tells a new customer, verbatim: "You can switch a
 * sandbox to a real plant later — the sample data is purged when you do." This
 * card is that sentence made true. Before it existed the sentence was a claim
 * about a capability the product did not have, which is the same failure class
 * as a fabricated figure — it just happened to be written in prose.
 *
 * Renders on demo orgs only. On a real plant there is nothing to offer: the
 * direction is one-way by design, because turning a plant with production
 * history back into a sandbox would invite sample data into a real record.
 */
/**
 * Money loop (G4 / S3.4) — the org's cost of a rejected casting. This is the one
 * number that lets a report turn "porosity, 12% of your rejects" into "porosity is
 * costing you $X". LEFT EMPTY it prices nothing: an unpriced org shows no dollar
 * figure anywhere, never a fabricated one. That is the whole point of the field, so
 * the empty state is first-class here — a blank input is "not priced", not zero.
 */
function ScrapCostCard() {
  const { activeOrgId } = useOrg();
  const toast = useToast();
  const [value, setValue] = useState("");
  const [perKg, setPerKg] = useState("");
  const [currencyInput, setCurrencyInput] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  const cost = useQuery({
    queryKey: ["scrap-cost", activeOrgId],
    queryFn: () => orgsApi.getScrapCost(),
    enabled: Boolean(activeOrgId) && isLiveApi,
  });

  // Seed the inputs from the server ONCE, so typing isn't clobbered on refetch.
  useEffect(() => {
    if (cost.data && !loaded) {
      setValue(cost.data.cost_per_reject == null ? "" : String(cost.data.cost_per_reject));
      setPerKg(cost.data.cost_per_kg == null ? "" : String(cost.data.cost_per_kg));
      setCurrencyInput(cost.data.currency ?? "");
      setLoaded(true);
    }
  }, [cost.data, loaded]);

  // WS-F3 / council INV-B3: there is NO "?? 'USD'" here any more, and that is the
  // point. `cost_currency` shipped NOT NULL DEFAULT 'USD' and no screen ever set
  // it, so every org was labelled in a currency it never chose — and the moment a
  // class-scoped INR rate is read against that label it renders a ~100× figure as
  // a fact. A currency is a customer answer; with none, nothing is priced.
  const savedCurrency = cost.data?.currency ?? null;
  const currencyPending = currencyInput.trim().toUpperCase();
  const currencyLooksValid = /^[A-Z]{3}$/.test(currencyPending);

  const save = async () => {
    const trimmedReject = value.trim();
    const trimmedKg = perKg.trim();
    const wantsARate = trimmedReject !== "" || trimmedKg !== "";
    setBusy(true);
    try {
      // A rate with no currency renders nothing, so the form refuses it here for
      // the same reason the API does — a save that "works" and changes nothing a
      // customer can see is the defect, not the guard.
      if (wantsARate && !currencyLooksValid) {
        toast.push(
          "error",
          "Enter a 3-letter currency (e.g. INR, USD, EUR) — a rate with no currency is not a price, and every figure built on it would be withheld.",
        );
        setBusy(false);
        return;
      }
      const nReject = trimmedReject === "" ? null : Number(trimmedReject);
      const nKg = trimmedKg === "" ? null : Number(trimmedKg);
      for (const n of [nReject, nKg]) {
        if (n !== null && (!Number.isFinite(n) || n < 0)) {
          toast.push("error", "Enter a cost of zero or more, or leave it blank to unprice.");
          setBusy(false);
          return;
        }
      }
      await orgsApi.setScrapCost({
        ...(nReject === null ? { clear_reject: true } : { cost_per_reject: nReject }),
        ...(nKg === null ? { clear_kg: true } : { cost_per_kg: nKg }),
        ...(currencyLooksValid ? { currency: currencyPending } : {}),
      });
      if (!wantsARate) {
        toast.push("ok", "Costs cleared — no dollar figure is shown anywhere.");
      } else {
        toast.push("ok", `Saved in ${currencyPending}.`);
      }
      await cost.refetch();
    } catch (err) {
      toast.push("error", backendErrorMessage(err, "the cost settings"));
    } finally {
      setBusy(false);
    }
  };

  return (
    // The briefs link here to rank their defects by money (B2: lib/brief/money-rank SET_COST_HREF).
    <Card id="scrap-cost" className="scroll-mt-4">
      <CardHeader className="flex flex-row items-start gap-3">
        <div className="flex size-9 items-center justify-center rounded-md bg-brand/10 text-brand">
          <Coins className="size-5" />
        </div>
        <div className="flex-1">
          <CardTitle>Cost of metal</CardTitle>
          <CardDescription>
            What a rejected casting and a kilogram of metal cost you. The cost per
            rejected casting is the default for every part without a cost of its own
            on its product: reports, the brief and the savings price scrap with it.
            The metal ledger prices unaccounted metal and written-off losses with the
            cost per kilogram. Leave either blank and no figure is shown for it: a
            cost we were never given is never invented.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cost-currency-input">Currency</Label>
          <div className="flex items-center gap-2">
            <Input
              id="cost-currency-input"
              placeholder="not set"
              maxLength={3}
              className="max-w-24 uppercase"
              value={currencyInput}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setCurrencyInput(e.target.value.toUpperCase())
              }
              disabled={busy || cost.isLoading}
            />
            <span className="text-xs text-on-surface-muted">
              {savedCurrency
                ? `Saved as ${savedCurrency}.`
                : "Not set — every cost figure on this org is withheld until you choose one."}
            </span>
          </div>
          <p className="text-xs text-on-surface-muted">
            Three letters, e.g. INR, USD, EUR. We do not assume one: a rate labelled
            in a currency you never chose is a wrong number that looks right.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="scrap-cost-input">
            Default cost per rejected casting{savedCurrency ? ` (${savedCurrency})` : ""}
          </Label>
          <Input
            id="scrap-cost-input"
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            placeholder="not priced"
            className="max-w-40"
            value={value}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
            disabled={busy || cost.isLoading}
          />
          <p className="text-xs text-on-surface-muted">
            {value.trim() === ""
              ? "Not priced. A part with a cost of its own on its product is priced at it; every other part shows scrap counts and rates, and no money figure."
              : `Every part without a cost of its own is priced at ${savedCurrency ?? (currencyPending || "?")} ${value.trim()} a rejected casting. A part's own cost, set on its product, comes first.`}
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cost-per-kg-input">
            Cost per kilogram of metal{savedCurrency ? ` (${savedCurrency})` : ""}
          </Label>
          <Input
            id="cost-per-kg-input"
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            placeholder="not priced"
            className="max-w-40"
            value={perKg}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setPerKg(e.target.value)}
            disabled={busy || cost.isLoading}
          />
          <p className="text-xs text-on-surface-muted">
            {perKg.trim() === ""
              ? "Not priced — the metal ledger shows kilograms only, and no cost."
              : "The metal ledger prices the unaccounted residual and any WRITTEN-OFF loss at this rate. Metal that was recovered to the charge or sold is never priced as a loss."}
          </p>
        </div>

        <div>
          <Button variant="secondary" disabled={busy || cost.isLoading} onClick={save}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>

        {/* Each part's own cost, from a file (B2): a product's cost comes before the default above. */}
        <PartCostsImport />
      </CardContent>
    </Card>
  );
}

function PromoteSandboxCard() {
  const { activeOrgId, activeOrg } = useOrg();
  const toast = useToast();
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const org = useQuery({
    queryKey: ["org-data-mode", activeOrgId],
    queryFn: () => orgsApi.get(activeOrgId!),
    enabled: Boolean(activeOrgId) && isLiveApi,
  });

  // Absent until we KNOW it is a sandbox. Rendering this on an org whose mode
  // hasn't loaded would flash "this is sample data" at someone looking at their
  // real plant.
  if (org.data?.data_mode !== "demo") return null;

  const promote = async () => {
    if (!activeOrgId) return;
    setBusy(true);
    try {
      const res = await orgsApi.promoteToReal(activeOrgId);
      // Everything on screen was computed from data that no longer exists.
      await qc.invalidateQueries();
      toast.push(
        "ok",
        res.already_real
          ? "This organisation was already a real plant."
          : `Now a real plant. Removed ${res.simulated_things_purged} simulated data ${
              res.simulated_things_purged === 1 ? "source" : "sources"
            }, ${res.simulated_readings_purged.toLocaleString()} sample readings and ${res.generated_genealogy_rows_purged.toLocaleString()} sample production records.`,
      );
    } catch (err) {
      toast.push("error", backendErrorMessage(err, "this organisation"));
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start gap-3">
        <div className="flex size-9 items-center justify-center rounded-md bg-warning/10 text-warning">
          <FlaskConical className="size-5" />
        </div>
        <div className="flex-1">
          <CardTitle>This is a sandbox</CardTitle>
          <CardDescription>
            Everything here is sample data — the readings, the heats, the
            defects. It is useful for seeing how the product works, and it must
            never be read as a measurement.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-on-surface-muted">
          When your own data is ready, switch this organisation to a real plant.
          All of the sample data is deleted first, and only then is the
          organisation relabelled — so it is never marked real while sample
          numbers are still on screen. Anything you imported from your own
          spreadsheets is kept.
        </p>
        <p className="text-sm text-on-surface-muted">
          This cannot be undone. A real plant can never be turned back into a
          sandbox.
        </p>
        <div>
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => setConfirmOpen(true)}
          >
            {busy ? "Switching…" : "Switch to a real plant"}
          </Button>
        </div>
      </CardContent>

      {/* Typed confirmation, not a plain OK. The action deletes every sample
          row in the organisation and cannot be reversed, so it should cost a
          deliberate act rather than a reflexive click. */}
      <DangerConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Switch to a real plant?"
        description={
          "Every piece of sample data in this organisation is deleted: simulated data sources, sample readings and sample production records. " +
          "Data you imported from your own spreadsheets is kept. This cannot be undone."
        }
        confirmPhrase={activeOrg?.orgName ?? "switch"}
        inputLabel={`Type "${activeOrg?.orgName ?? "switch"}" to confirm`}
        confirmLabel="Delete the sample data and switch"
        onConfirm={promote}
      />
    </Card>
  );
}

function DangerZone() {
  const { canAdmin, activeOrg, activeOrgId, orgs, setActiveOrg } = useOrg();
  const isOwner = useIsActiveOrgOwner();
  const toast = useToast();
  const qc = useQueryClient();

  const [clearOpen, setClearOpen] = useState(false);
  const [deleteOrgOpen, setDeleteOrgOpen] = useState(false);

  // Nothing to show if the user can neither clear the process (admin) nor
  // delete the org (owner) — keeps the section out of viewers' way.
  if (!canAdmin && !isOwner) return null;

  // "Delete entire process" — POST /v1/twin/plant/clear. Clears the twin model
  // (entities/connections/bindings) + derived heats/genealogy/correlations,
  // KEEPS the org/things/dashboards. Invalidate every twin surface so they
  // empty out without a reload.
  const clearProcess = async () => {
    try {
      const res = await twinPlants.clear();
      void qc.invalidateQueries({ queryKey: TWIN_ENTITIES_KEY });
      void qc.invalidateQueries({ queryKey: TWIN_CONNECTIONS_KEY });
      void qc.invalidateQueries({ queryKey: TWIN_BATCHES_KEY });
      void qc.invalidateQueries({ queryKey: TWIN_BATCHES_ALL_KEY });
      void qc.invalidateQueries({ queryKey: TWIN_BATCH_PARTS_KEY });
      void qc.invalidateQueries({ queryKey: TWIN_STAGES_KEY });
      void qc.invalidateQueries({ queryKey: ["twin-correlations"] });
      void qc.invalidateQueries({ queryKey: ["twin-plant"] });
      toast.push(
        "ok",
        `Process cleared — ${res.entities} entities, ${res.connections} connections, ${res.heats} heats removed.`,
      );
    } catch (err) {
      toast.push("error", backendErrorMessage(err, "the process model"));
    } finally {
      setClearOpen(false);
    }
  };

  // "Delete organisation" — DELETE /v1/orgs/{activeOrgId}. Hard delete (owner
  // only). On success, move to another membership (or onboarding when none
  // remain). The org list lives on the auth identity (orgMemberships); a hard
  // reload via setActiveOrg + assign re-resolves everything under the new org.
  const deleteOrg = async () => {
    if (!activeOrgId) return;
    try {
      await orgsApi.remove(activeOrgId);
      const remaining = orgs.filter((o) => o.orgId !== activeOrgId);
      toast.push("ok", `Deleted ${activeOrg?.orgName ?? "organisation"}.`);
      if (remaining.length > 0) {
        // Switch to another org — setActiveOrg persists + sets X-Org-Id; the
        // hard reload re-resolves identity (the deleted org drops out of the
        // membership list) and every data provider under the new org.
        setActiveOrg(remaining[0].orgId);
        window.location.assign("/app");
      } else {
        // No orgs left → the no-org guard would route to onboarding anyway.
        window.location.assign("/onboarding");
      }
    } catch (err) {
      toast.push("error", backendErrorMessage(err, "this organisation"));
      setDeleteOrgOpen(false);
    }
  };

  return (
    <Card className="border-danger/40">
      <CardHeader className="flex flex-row items-start gap-3">
        <div className="flex size-9 items-center justify-center rounded-md bg-danger/10 text-danger">
          <AlertTriangle className="size-5" />
        </div>
        <div className="flex-1">
          <CardTitle className="text-danger">Danger zone</CardTitle>
          <CardDescription>
            Irreversible actions. Clearing the process wipes your plant model
            and its heats; deleting the organisation removes everything.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {/* Clear the process model — admins. */}
        {canAdmin && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface p-3">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex items-center gap-1.5 text-sm font-medium text-on-surface">
                <Network className="size-4 text-on-surface-muted" />
                Delete entire process
              </span>
              <span className="text-xs text-on-surface-muted">
                Removes the plant model (its machines, their connections and
                the tags linked to them) and every heat, with all that was
                worked out from them. Keeps the organisation, its gateways
                and its dashboards.
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={!isLiveApi}
              onClick={() => setClearOpen(true)}
              title={
                isLiveApi
                  ? "Clear the plant model and the heats of this organisation"
                  : "Available with the live backend"
              }
              className="text-danger hover:bg-danger/10"
            >
              <Trash2 className="mr-1.5 size-4" />
              Delete process
            </Button>
          </div>
        )}

        {/* Delete the organisation — OWNER only (typed confirmation). */}
        {isOwner && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-danger/40 bg-danger/5 p-3">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex items-center gap-1.5 text-sm font-medium text-on-surface">
                <Building2 className="size-4 text-danger" />
                Delete organisation
              </span>
              <span className="text-xs text-on-surface-muted">
                Permanently deletes <span className="font-medium text-on-surface">{activeOrg?.orgName}</span> and
                all of its data — gateways, twin model, dashboards and members.
                This cannot be undone.
              </span>
            </div>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setDeleteOrgOpen(true)}
            >
              <Trash2 className="mr-1.5 size-4" />
              Delete organisation
            </Button>
          </div>
        )}
      </CardContent>

      <ConfirmDialog
        open={clearOpen}
        onClose={() => setClearOpen(false)}
        onConfirm={clearProcess}
        title="Delete the entire process?"
        description={
          <>
            This clears the process graph (entities, connections and tag
            bindings) and every heat, genealogy record and correlation for{" "}
            <span className="font-medium text-on-surface">{activeOrg?.orgName}</span>.
            Your gateways, their data and dashboards are kept. This can&apos;t be
            undone.
          </>
        }
        confirmLabel="Delete process"
        destructive
      />

      <DangerConfirmDialog
        open={deleteOrgOpen}
        onClose={() => setDeleteOrgOpen(false)}
        onConfirm={deleteOrg}
        title="Delete this organisation?"
        description={
          <>
            This permanently deletes <span className="font-medium text-on-surface">{activeOrg?.orgName}</span>{" "}
            and everything in it. This action is irreversible.
          </>
        }
        confirmPhrase={activeOrg?.orgName ?? ""}
        confirmLabel="Delete organisation"
      />
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────
// Tab strip
// ────────────────────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Users;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors",
        // The open tab reads in the page's own text, underlined in it: the graphite brand on its surface reads at about
        // 1.4:1 in the dark theme, as text (the menu's #1211 fix) and as a line alike. The line is an inset shadow in
        // the text's own colour, so it keeps the text's contrast. The hubs' strip of tabs does the same.
        active
          ? "text-on-surface shadow-[inset_0_-2px_0_0_currentColor] forced-colors:underline forced-colors:underline-offset-4"
          : "text-on-surface-muted hover:text-on-surface",
      )}
    >
      <Icon className="size-4" />
      {label}
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────
// Packs panel
// ────────────────────────────────────────────────────────────────────

/**
 * The org's packs, read only (Navigation review D10, 2026-10-06): Sahas switches a pack on for an org on the platform
 * console; this page says which are on. A live org's are the server's (GET /v1/platform/packs/enabled); the sample's
 * are the browser's.
 */
function PacksPanel() {
  const tenant = useTenantTheme();
  const server = useEnabledPacksQuery();
  const enabledPacks = isLiveApi ? (server.data?.packs ?? []).map((p) => p.pack) : tenant.enabledPacks;
  // The org's ACTUAL plant archetype (#118), from its provisioned plant.
  const { label: archetypeLabel, isDefault: archetypeIsDefault } =
    useArchetypeLabel();
  return (
    <Card>
      <CardHeader className="flex flex-row items-start gap-3">
        <div className="flex size-9 items-center justify-center rounded-md bg-brand-muted text-brand">
          <PackageOpen className="size-5" />
        </div>
        <div className="flex-1">
          <CardTitle>Industry packs</CardTitle>
          <CardDescription>
            A pack unlocks its equipment templates, dashboards and the AI casting consultant&apos;s grounding. Sahas
            switches packs on for the org; ask your Sahas contact to change them.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {/* This org's real plant archetype — sourced per-org from its
            provisioned plant (the plants table), NOT a hardcoded "grey iron".
            Falls back to the seeded grey-iron default for orgs with no plant. */}
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
            This org&apos;s plant
          </span>
          <Badge variant="info" className="text-[10px]">
            {archetypeLabel}
          </Badge>
          {archetypeIsDefault && (
            <span className="text-[10px] text-on-surface-muted">
              default — no plant provisioned yet
            </span>
          )}
        </div>
        {AVAILABLE_PACKS.map((pack) => {
          const enabled = enabledPacks.includes(pack.id);
          const installable = pack.ships.startsWith("v1");
          return (
            <div
              key={pack.id}
              className={cn(
                "flex items-start justify-between gap-4 rounded-lg border border-border bg-surface-elevated p-3",
                enabled && "border-brand/60 bg-brand-muted/15",
              )}
            >
              <div className="flex flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-on-surface">{pack.name}</span>
                  <Badge variant="secondary" className="text-[10px]">
                    {pack.tier}
                  </Badge>
                  <Badge
                    variant={installable ? "success" : "warning"}
                    className="text-[10px]"
                  >
                    {pack.ships}
                  </Badge>
                  {enabled && (
                    <Badge variant="default" className="text-[10px]">
                      <CheckCircle2 className="mr-1 size-3" />
                      Enabled
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-on-surface-muted">{pack.description}</p>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────
// Security panel — your name, change password + vendor data visibility
// ────────────────────────────────────────────────────────────────────

function SecurityPanel({ canAdmin }: { canAdmin: boolean }) {
  return (
    <div className="flex flex-col gap-4">
      {/* The person's own display name, the one reports and comments carry. */}
      <YourNameCard />
      <ChangePasswordCard />
      {/* Only rendered when the org actually has a vendor (self-hides). */}
      <VendorVisibilityCard canAdmin={canAdmin} />
    </div>
  );
}

/**
 * Map a change-password failure to user-facing copy off the stable backend
 * error `code` (on `.body.error`, the same envelope /v1/platform/auth/* uses).
 * `invalid_credentials` (401) → wrong current password; `weak_password` (400)
 * → the new one fails the complexity policy; anything else → generic.
 */
function changePasswordErrorMessage(err: unknown): string {
  const code =
    err instanceof ApiClientError
      ? (err.body as { error?: string } | null)?.error
      : undefined;
  if (code === "invalid_credentials") {
    return "Your current password is incorrect.";
  }
  if (code === "weak_password") {
    return "New password doesn't meet the complexity policy (min 8 chars, upper, lower, digit, symbol).";
  }
  return "Couldn't change your password. Please try again.";
}

/**
 * Self-service password change — POST /v1/platform/me/change-password. Shown to
 * every signed-in user (it's their own password). Import-provisioned foundry
 * admins land here with a temporary password and set their own. Client-side we
 * require the three fields non-empty and new === confirm; the server verifies
 * the current password and enforces the complexity policy.
 */
function ChangePasswordCard() {
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [nextPw, setNextPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    if (!current || !nextPw || !confirmPw) {
      toast.push("error", "Fill in all three password fields.");
      return;
    }
    if (nextPw !== confirmPw) {
      toast.push("error", "New password and confirmation don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await orgsApi.changePassword(current, nextPw);
      toast.push("ok", "Password changed — use it the next time you sign in.");
      setCurrent("");
      setNextPw("");
      setConfirmPw("");
    } catch (err) {
      toast.push("error", changePasswordErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start gap-3">
        <div className="flex size-9 items-center justify-center rounded-md bg-brand-muted text-brand">
          <KeyRound className="size-5" />
        </div>
        <div className="flex-1">
          <CardTitle>Change password</CardTitle>
          <CardDescription>
            Update the password you use to sign in. Handed a temporary password?
            Set your own here.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex max-w-sm flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="current-password">Current password</Label>
            <Input
              id="current-password"
              name="current-password"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              disabled={submitting}
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              name="new-password"
              type="password"
              autoComplete="new-password"
              value={nextPw}
              onChange={(e) => setNextPw(e.target.value)}
              disabled={submitting}
              required
            />
            <span className="text-[11px] text-on-surface-muted">
              At least 8 characters, with upper- and lower-case letters, a
              number, and a symbol.
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="confirm-password">Confirm new password</Label>
            <Input
              id="confirm-password"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              disabled={submitting}
              required
            />
          </div>
          <div>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Changing…" : "Change password"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * Vendor data-visibility toggle — GET/PUT /v1/platform/vendor-visibility.
 * Rendered ONLY when the active org has a vendor parent (has_vendor). The
 * toggle enables on canAdmin; non-admins see the current state read-only. The
 * PUT returns the same shape as the GET, so we push it back into the query
 * cache to reflect the new value.
 */
function VendorVisibilityCard({ canAdmin }: { canAdmin: boolean }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);

  const vis = useQuery({
    queryKey: ["vendor-visibility"],
    queryFn: () => orgsApi.getVendorVisibility(),
    enabled: isLiveApi,
  });

  const data = vis.data;
  // No card in stub mode, while the first read is in flight / errored, or for a
  // top-level org with no vendor parent.
  if (!isLiveApi || !data || !data.has_vendor) return null;

  const vendorName = data.vendor_name ?? "your vendor";
  const visible = data.visible;

  const toggle = () => {
    if (!canAdmin || saving) return;
    const nextVisible = !visible;
    setSaving(true);
    orgsApi
      .setVendorVisibility(nextVisible)
      .then((res) => {
        qc.setQueryData<VendorVisibility>(["vendor-visibility"], res);
        toast.push(
          "ok",
          nextVisible
            ? `${res.vendor_name ?? "Your vendor"} can now see this plant's instrument readings.`
            : `Instrument readings are now hidden from ${res.vendor_name ?? "your vendor"}.`,
        );
      })
      .catch(() =>
        toast.push("error", "Couldn't update vendor data visibility."),
      )
      .finally(() => setSaving(false));
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start gap-3">
        <div className="flex size-9 items-center justify-center rounded-md bg-brand-muted text-brand">
          <Eye className="size-5" />
        </div>
        <div className="flex-1">
          <CardTitle>Vendor data visibility</CardTitle>
          <CardDescription>
            Control whether {vendorName} can see this plant&apos;s instrument
            reading values.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-surface p-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-sm font-medium text-on-surface">
              Share instrument readings with {vendorName}
            </span>
            <span className="text-xs text-on-surface-muted">
              When on, {vendorName} can see this plant&apos;s instrument readings
              for the equipment they supplied. Turn it off to hide your readings
              from {vendorName}. They keep seeing device health / status either
              way.
            </span>
            {!canAdmin && (
              <span className="text-[11px] text-on-surface-muted">
                {visible
                  ? `Visible to ${vendorName}.`
                  : `Hidden from ${vendorName}.`}{" "}
                Only org admins can change this.
              </span>
            )}
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={visible}
            aria-label={`Share instrument readings with ${vendorName}`}
            onClick={toggle}
            disabled={!canAdmin || saving}
            title={
              !canAdmin
                ? "Only org admins can change this."
                : visible
                  ? "Turn off — hide readings from your vendor"
                  : "Turn on — share readings with your vendor"
            }
            className={cn(
              "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
              visible ? "bg-brand" : "bg-surface-muted",
              (!canAdmin || saving) && "cursor-not-allowed opacity-60",
            )}
          >
            <span
              className={cn(
                "inline-block size-4 transform rounded-full bg-surface shadow transition-transform",
                visible ? "translate-x-4" : "translate-x-0.5",
              )}
            />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────
// Branding panel
// ────────────────────────────────────────────────────────────────────

const PALETTE_SLOTS: Array<{
  key: keyof PaletteOverride;
  label: string;
  description: string;
}> = [
  { key: "brand", label: "Brand", description: "Primary CTAs + active nav" },
  { key: "accent", label: "Accent", description: "Secondary CTAs + hover" },
  { key: "success", label: "Success", description: "Pass / healthy / accepted" },
  { key: "warning", label: "Warning", description: "Watch / drift / pending" },
  { key: "danger", label: "Danger", description: "Fail / out-of-spec / alarm" },
  { key: "info", label: "Info", description: "Neutral status / hints" },
];

function BrandingPanel() {
  const tenant = useTenantTheme();
  const admin = useTenantThemeAdmin();
  const { activeOrg } = useOrg();

  return (
    <div className="flex flex-col gap-4">
      {/* The look is the org's (saved with it by an org admin); an edit is a draft until then. */}
      <OrgLookBar orgName={activeOrg?.orgName} state={admin.orgLook} />
      {/* Preset gallery */}
      <Card>
        <CardHeader className="flex flex-row items-start gap-3">
          <div className="flex size-9 items-center justify-center rounded-md bg-brand-muted text-brand">
            <Palette className="size-5" />
          </div>
          <div className="flex-1">
            <CardTitle>Start from a preset</CardTitle>
            <CardDescription>
              Curated looks: one click applies the palette, typography, density, radius and background. Override any
              setting below, then save the look for the org.
            </CardDescription>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => admin.reset()}
            title="Back to the default look (grey-blue), to save for the org"
          >
            <RotateCcw className="mr-1.5 size-3.5" />
            Reset
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {admin.availablePresets.map((preset) => {
              const active = tenant.presetId === preset.id;
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => admin.applyPreset(preset.id)}
                  className={cn(
                    "flex flex-col gap-2 rounded-md border p-3 text-left transition-colors",
                    active
                      ? "border-brand bg-brand-muted/30"
                      : "border-border bg-surface hover:border-brand/60",
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-on-surface">
                      {preset.name}
                    </span>
                    {preset.mode === "dark" && (
                      <Moon className="size-3 text-on-surface-muted" />
                    )}
                  </div>
                  <span className="text-[11px] text-on-surface-muted">
                    {preset.blurb}
                  </span>
                  <div className="flex gap-1">
                    {(["brand", "accent", "success", "warning", "danger"] as const).map(
                      (slot) => {
                        const rgb = preset.theme.palette?.[slot];
                        if (!rgb) return null;
                        return (
                          <span
                            key={slot}
                            className="size-4 rounded-full border border-border"
                            style={{ background: `rgb(${rgb})` }}
                            title={slot}
                          />
                        );
                      },
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Mode + identity */}
      <Card>
        <CardHeader>
          <CardTitle>Identity</CardTitle>
          <CardDescription>
            The org&apos;s logo, and your own light or dark mode. The product&apos;s name is always Foundry Twin.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <LogoUploader
            logoUrl={tenant.logoUrl}
            onChange={admin.setLogoUrl}
          />

          <div>
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
              Theme mode
            </p>
            <div className="flex gap-1.5">
              <ModeButton
                active={tenant.mode === "light"}
                onClick={() => admin.setMode("light")}
                icon={Sun}
                label="Light"
              />
              <ModeButton
                active={tenant.mode === "dark"}
                onClick={() => admin.setMode("dark")}
                icon={Moon}
                label="Dark"
              />
              <ModeButton
                active={tenant.mode === "system"}
                onClick={() => admin.setMode("system")}
                icon={Monitor}
                label="System"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Palette */}
      <Card>
        <CardHeader>
          <CardTitle>Palette</CardTitle>
          <CardDescription>
            Six recolourable slots. Foreground + muted tints derive automatically
            from each base; pick contrast-safe values.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2">
            {PALETTE_SLOTS.map(({ key, label, description }) => (
              <ColorSlot
                key={key}
                label={label}
                description={description}
                value={tenant.palette[key] ?? null}
                onChange={(rgb) => admin.setPaletteSlot(key, rgb)}
              />
            ))}
          </div>
          <PaletteLivePreview />
        </CardContent>
      </Card>

      {/* Typography + density + radius + pattern */}
      <Card>
        <CardHeader>
          <CardTitle>Aesthetics</CardTitle>
          <CardDescription>
            Font, density, corner radius, and an optional background pattern.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          <LabelledField label="Typography">
            <select
              value={tenant.typography}
              onChange={(e) =>
                admin.setTypography(e.target.value as TypographyPreset)
              }
              className="h-9 w-full rounded-md border border-border bg-surface px-2 text-sm text-on-surface"
            >
              {(Object.keys(TYPOGRAPHY_LABEL) as TypographyPreset[]).map((k) => (
                <option key={k} value={k}>
                  {TYPOGRAPHY_LABEL[k]}
                </option>
              ))}
            </select>
          </LabelledField>

          <SegmentedField label="Density">
            {(Object.keys(DENSITY_SCALE) as DensityPreset[]).map((d) => (
              <SegmentedButton
                key={d}
                active={tenant.density === d}
                onClick={() => admin.setDensity(d)}
              >
                {d}
              </SegmentedButton>
            ))}
          </SegmentedField>

          <SegmentedField label="Corner radius">
            {(Object.keys(RADIUS_SCALE) as RadiusPreset[]).map((r) => (
              <SegmentedButton
                key={r}
                active={tenant.radius === r}
                onClick={() => admin.setRadius(r)}
              >
                {r}
              </SegmentedButton>
            ))}
          </SegmentedField>

          <SegmentedField label="Background pattern">
            {(Object.keys(PATTERN_LABEL) as BackgroundPattern[]).map((p) => (
              <SegmentedButton
                key={p}
                active={tenant.pattern === p}
                onClick={() => admin.setPattern(p)}
              >
                {PATTERN_LABEL[p]}
              </SegmentedButton>
            ))}
          </SegmentedField>
        </CardContent>
      </Card>

      {/* Aesthetic preview */}
      <Card>
        <CardHeader className="flex flex-row items-start gap-3">
          <div className="flex size-9 items-center justify-center rounded-md bg-brand-muted text-brand">
            <Type className="size-5" />
          </div>
          <div>
            <CardTitle>Live preview</CardTitle>
            <CardDescription>
              The whole app updates as you change settings — this strip shows the
              key surfaces at a glance.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <AestheticPreview />
        </CardContent>
      </Card>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Branding sub-components
// ────────────────────────────────────────────────────────────────────

function ModeButton({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Sun;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-1 items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-xs font-medium transition-colors",
        active
          ? "border-brand bg-brand text-brand-foreground"
          : "border-border bg-surface text-on-surface hover:border-brand/60",
      )}
    >
      <Icon className="size-3.5" />
      {label}
    </button>
  );
}

function LabelledField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
        {label}
      </span>
      {children}
    </label>
  );
}

function SegmentedField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
        {label}
      </span>
      <div className="flex gap-1">{children}</div>
    </div>
  );
}

function SegmentedButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex-1 rounded-md border px-2 py-1.5 text-xs font-medium capitalize transition-colors",
        active
          ? "border-brand bg-brand text-brand-foreground"
          : "border-border bg-surface text-on-surface hover:border-brand/60",
      )}
    >
      {children}
    </button>
  );
}

function LogoUploader({
  logoUrl,
  onChange,
}: {
  logoUrl: string | null;
  onChange: (url: string | null) => void;
}) {
  // How the logo is kept is a staff matter (the 2026-10-24 page sweep, H4).
  const { isPlatformAdmin } = useOrg();
  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Read as a data URL into the org's look (saved with the org on Save). Capped at about 200 KB.
    if (file.size > 200_000) {
      // eslint-disable-next-line no-alert
      alert("The logo is too large. Keep it under 200 KB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") onChange(reader.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
        Logo
      </p>
      <div className="flex items-center gap-3 rounded-md border border-border bg-surface p-3">
        <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-surface-elevated">
          {logoUrl ? (
            // eslint-disable-next-line jsx-a11y/img-redundant-alt
            <img src={logoUrl} alt="Logo preview" className="max-h-full max-w-full object-contain" />
          ) : (
            <ImagePlus className="size-5 text-on-surface-muted" />
          )}
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <span className="text-xs text-on-surface">
            {logoUrl ? "Custom logo uploaded" : "No logo set, so the standard mark is used."}
          </span>
          <span className="text-[10px] text-on-surface-muted">
            PNG or SVG, with a transparent background, under 200 KB.{isPlatformAdmin ? " Stored locally for now." : ""}
          </span>
        </div>
        <label className="cursor-pointer">
          <input
            type="file"
            accept="image/png,image/svg+xml,image/jpeg,image/webp"
            className="hidden"
            onChange={handleFile}
          />
          <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-1 text-xs hover:border-brand">
            <Upload className="size-3.5" />
            Upload
          </span>
        </label>
        {logoUrl && (
          <Button size="sm" variant="ghost" onClick={() => onChange(null)}>
            Remove
          </Button>
        )}
      </div>
    </div>
  );
}

function ColorSlot({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description: string;
  value: string | null;
  onChange: (rgb: string | null) => void;
}) {
  // Convert the "R G B" CSS-var format ↔ "#rrggbb" for the native picker.
  const hex = rgbToHex(value);

  const handlePick = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(hexToRgb(e.target.value));
  };

  return (
    <div className="flex items-center gap-3 rounded-md border border-border bg-surface p-2">
      <label className="cursor-pointer">
        <input
          type="color"
          value={hex ?? "#888888"}
          onChange={handlePick}
          className="size-10 cursor-pointer rounded-md border border-border bg-transparent"
        />
      </label>
      <div className="flex flex-1 flex-col">
        <span className="text-xs font-semibold text-on-surface">{label}</span>
        <span className="text-[10px] text-on-surface-muted">{description}</span>
      </div>
      {value && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-[10px] text-on-surface-muted hover:text-danger"
          title="Reset to default"
        >
          reset
        </button>
      )}
    </div>
  );
}

function PaletteLivePreview() {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border bg-surface-elevated/40 p-3">
      <Button size="sm" variant="primary">
        Primary
      </Button>
      <Button size="sm" variant="outline">
        Outline
      </Button>
      <Badge variant="success">Success</Badge>
      <Badge variant="warning">Warning</Badge>
      <Badge variant="danger">Danger</Badge>
      <Badge variant="info">Info</Badge>
      <span className="ml-auto text-[10px] text-on-surface-muted">
        Live — every change applies across the whole app.
      </span>
    </div>
  );
}

function AestheticPreview() {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {/* Type sample */}
      <div className="flex flex-col gap-1.5 rounded-md border border-border bg-surface-elevated p-4">
        <h3 className="text-xl font-semibold text-on-surface">Heading sample</h3>
        <p className="text-sm text-on-surface">
          The quick brown fox jumps over the lazy dog. 0123456789.
        </p>
        <p className="text-xs text-on-surface-muted">
          Captions + helper text at a comfortable reading scale.
        </p>
        <code className="mt-1 inline-block rounded bg-surface px-2 py-1 font-mono text-[11px] text-on-surface">
          Tag names show in this type: sand_mixer.moisture
        </code>
      </div>

      {/* Component sample */}
      <div className="flex flex-col gap-2 rounded-md border border-border bg-surface-elevated p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-on-surface">Card sample</span>
          <Badge variant="success">healthy</Badge>
        </div>
        <p className="text-xs text-on-surface-muted">
          A representative tile — same surfaces, borders, and radius the app uses.
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="primary">
            Save
          </Button>
          <Button size="sm" variant="outline">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Summary cards — unchanged from B0.25
// ────────────────────────────────────────────────────────────────────


// ────────────────────────────────────────────────────────────────────
// Hex ↔ "R G B" conversion helpers
// ────────────────────────────────────────────────────────────────────

// ────────────────────────────────────────────────────────────────────
// Org-at-a-glance summary cards (live counts only)
// ────────────────────────────────────────────────────────────────────

/** Two cards of REAL org counts: members + pending invites, sites + data
 *  sources. Each number comes from an existing API (no new backend); cards
 *  whose data has no source — billing, audit log, data residency — are simply
 *  not shown (no fake placeholders). Admin-gated by the caller. */
function OrgSummaryCards() {
  const { activeOrgId } = useOrg();
  const orgId = activeOrgId ?? "";
  const members = useQuery({
    queryKey: ["admin-members", orgId],
    queryFn: () => membersApi.list(orgId),
    enabled: !!orgId,
  });
  const invites = useQuery({
    queryKey: ["admin-invites", orgId],
    queryFn: () => invitesApi.list(orgId),
    enabled: !!orgId,
  });
  const sites = useQuery({
    queryKey: ["admin-sites", orgId],
    queryFn: () => sitesApi.list(),
    enabled: !!orgId,
  });
  const things = useThingsQuery();

  const n = (v: number | undefined, loading: boolean) =>
    loading ? "…" : String(v ?? 0);

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <SummaryCard
        icon={Users}
        title="Users & roles"
        stats={[
          { label: "Members", value: n(members.data?.total, members.isLoading) },
          { label: "Pending invites", value: n(invites.data?.total, invites.isLoading) },
        ]}
        status="Members and their access roles in this organisation."
      />
      <SummaryCard
        icon={Building2}
        title="Org structure"
        stats={[
          { label: "Sites", value: n(sites.data?.total, sites.isLoading) },
          { label: "Data sources", value: n(things.data?.total, things.isLoading) },
        ]}
        status="Sites and connected data sources (gateways + instruments) in this org."
      />
    </div>
  );
}

function SummaryCard({
  icon: Icon,
  title,
  stats,
  status,
}: {
  icon: typeof Users;
  title: string;
  stats: Array<{ label: string; value: string }>;
  status: string;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 pt-4">
        <div className="flex items-center gap-2">
          <div className="flex size-7 items-center justify-center rounded-md bg-brand-muted text-brand">
            <Icon className="size-4" />
          </div>
          <span className="font-semibold text-on-surface">{title}</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {stats.map((s) => (
            <div
              key={s.label}
              className="rounded-md border border-border bg-surface-elevated px-2 py-1.5"
            >
              <span className="block text-[10px] uppercase tracking-wide text-on-surface-muted">
                {s.label}
              </span>
              <span className="text-sm font-medium text-on-surface">{s.value}</span>
            </div>
          ))}
        </div>
        <p className="border-t border-border pt-2 text-[11px] text-on-surface-muted">
          {status}
        </p>
      </CardContent>
    </Card>
  );
}

function rgbToHex(rgb: string | null): string | null {
  if (!rgb) return null;
  const parts = rgb.split(/\s+/).map((s) => Number(s));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  return (
    "#" +
    parts
      .map((n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0"))
      .join("")
  );
}

function hexToRgb(hex: string): string {
  const m = hex.replace("#", "");
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
}
